<script>
import { scaleLinear, color, hsl } from "d3";
import { TweenLite } from "gsap";
import Draggable from 'gsap/Draggable';
export default {
  name: 'slider',
  props: {
    colours: {
      type: Array
    },
    disabled: Boolean,
    grid: { type: Boolean, default: true },
    gridDivisions: { type: Number, default: 10 },
    labels: Object,
    min: {
      // minimum possible value
      type: Number,
      default: 0
    },
    max: {
      // maximum possible value
      type: Number,
      default: 10
    },
    popover: {
      type: Boolean,
      default: true
    },
    prefix: { type: String, default: '' }, // Set prefix for values. Will be set up right before the number: **$**100
    postfix: { type: String, default: '' }, // Set postfix for values. Will be set up right after the number: 100k
    range: Boolean,
    reverse: Boolean,
    step: {
      // Set sliders step. Always > 0. Could be fractional
      type: Number,
      default: 1,
      validator: (value) => { return value >= 0 }
    },
    colourPopover: Boolean,
    colourHandle: Boolean,
    value: {},
    vertical: Boolean,
  },
  render(h) {
    /* Define props objects */
    var sliderContainerProps = {
      class: ['slider-container', { 'slider-container-vertical': this.vertical, 'disabled': this.disabled, 'unanswered': this.value === null }],
      ref: 'sliderContainer'
    }
    var popoverPosition = this.posPx + this.handleRadius
    var popoverProps = {
      class: ['slider-popover'],
      ref: 'popover',
      style: {
        [this.vertical ? 'top' : 'left']: popoverPosition + 'px'
      }
    }
    if (this.$refs.popover) {
      var popoverSize = this.$refs.popover.getBoundingClientRect()[this.vertical ? 'height' : 'width']
      popoverProps.style[this.vertical ? 'top' : 'left'] = this.posPx - (popoverSize / 2) + this.handleRadius + 'px'
      if (this.colours && this.colourPopover) popoverProps.style.background = this.getColourFromValue(this.value)
    }
    var sliderProps = { class: ['slider'], on: { 'resize': this.resize, '!click': this.clickSliderTrack }, ref: 'slider', }
    var sliderTrackProps = { class: 'slider-track', ref: 'sliderTrack' }
    var handleProps = { class: 'handle', ref: 'handle', style: {} }
    var sliderBarProps = { class: 'slider-bar', ref: 'sliderBar', style: {} }
    if (this.colours) {
      if (this.colourHandle) {
        var colour = this.getColourFromValue(this.value)
        var d3Col = hsl(color(colour))
        d3Col.s = d3Col.s * 3
        d3Col.l = d3Col.l / 3
        handleProps.style.border = 'none';
        handleProps.style.background = 'radial-gradient(' + colour + ' 0%, ' + d3Col + ' 100%)'
      }
      sliderBarProps.style = {
        'background': this.getGradient(),
        'background-size': this.getGradientSize(),
        'background-position': this.vertical && this.reverse ? 'bottom' : this.vertical ? 'top' : this.reverse ? 'right' : 'left'
      }
    }
    var gridProps = { class: ['grid', { 'grid-vertical': this.vertical, 'grid-reverse': this.reverse }], ref: 'grid' }
    var rangeLabelProps = {}
    /* define render functions */
    var popover = h('div', { class: 'popover-track' }, [h('div', popoverProps, [this.getDisplayValue(this.value)])])
    var sliderChildren = [h('div', sliderTrackProps)];
    sliderChildren.push(h('div', sliderBarProps))
    sliderChildren.push(h('div', handleProps))
    var slider = h('div', sliderProps, sliderChildren)
    var grid = h('div', gridProps, this.makeGrid(h))
    var sliderContainerChildren = []
    sliderContainerChildren = [popover, slider, grid]
    if (this.labels) {
      sliderContainerChildren.push(
        h('div', rangeLabelProps, [h('div', { class: 'float-left' }, [this.labels.min]), h('div', { class: 'float-right' }, [this.labels.max])])
      )
    }
    var sliderContainer = h('div', sliderContainerProps, sliderContainerChildren)
    return sliderContainer
  },
  data() {
    return {
      bounds: {},
      handleRadius: 10,
      posPx: 0,
      minPx: 0,
      maxPx: 0,
      clientX: 0,
      clientY: 0,
      dimension: this.vertical ? 'Y' : 'X',
    }
  },
  mounted() {
    window.addEventListener('resize', this.resize)
    this.setupSlider()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
  },
  computed: {
    posValue() {
      if (this.handle) {
        var val = 0;
        if (this.reverse) {
          val = this.max + (this.posPx / this.maxPx * (this.max - this.min) * -1)
        } else {
          val = this.min + ((this.posPx / this.maxPx) * (this.max - this.min))
        }
        return parseFloat(Number.parseFloat(val).toFixed(this.stepDepth))
      }
    },
    stepDepth() {
      return (this.step + "").split(".").length == 2 ? (this.step + "").split(".")[1].length : 0
    }
  },
  methods: {
    setupSlider() {
      this.handle = Draggable.create(this.$refs.handle, {
        type: this.dimension,
        bounds: this.$refs.slider,
        throwProps: true,
        // liveSnap:this.steps,
        // onClick: this.clickHandle,
        onDrag: this.setPosition,
        onDragEnd: this.setPositionDrop,
      })[0];
      this.minPx = this.handle['min' + this.dimension]
      this.maxPx = this.handle['max' + this.dimension]
      this.handleRadius = parseInt(this.handle.target.getBoundingClientRect()[this.vertical ? 'height' : 'width'] / 2)
      this.updateSliderSize()
      this.setPositionFromValue()

    },
    resize() {
      if (this['client' + this.dimension] == this.$el[this.vertical ? 'clientHeight' : 'clientWidth']) return;
      this.handleRadius = parseInt(this.handle.target.getBoundingClientRect()[this.vertical ? 'height' : 'width'] / 2)
      this.handle.applyBounds(this.$refs.slider)
      this.updateSliderSize()
      this.setPositionFromValue()
    },
    makeGrid(h) {
      var nodes = []
      var divisions = this.gridDivisions;
      if (this.max - this.min < divisions) divisions = this.max - this.min
      for (var i = 0; i <= divisions; i++) {
        var gridNum = h('span', { class: 'grid-num' }, [this.getDisplayValue(this.getGridValue(i, divisions))])
        nodes.push(h('div', { class: 'grid-unit' }, [gridNum]))
      };
      return nodes
    },
    getGradient() {
      var direction = this.vertical && this.reverse ? 'top' : this.vertical ? 'bottom' : this.reverse ? 'left' : 'right'
      var stops = this.colours.length - 1
      var grads = this.colours.map((col, i) => {
        return col + ' ' + (i / stops * 100) + '%'
      })
      var gradStr = 'linear-gradient(to ' + direction + ', ' + grads.join(',') + ')';
      return gradStr
    },
    getGradientSize() {
      var gradPc = ''
      if (this.posPx && this.colours) {
        var Pc = (this.posPx / this.maxPx)
        if (this.reverse) { Pc = 1 - Pc }
        gradPc = 100 / Pc + '%'
        gradPc = this.vertical ? '100% ' + gradPc : gradPc + ' 100%'
        return gradPc
      }
      return ''
    },
    getGridValue(i, divisions) {
      return scaleLinear()
        .domain([0, divisions])
        .range([this.min, this.max])(i);
    },
    getDisplayValue(value) {
      if (value != null) {
        var val = Number.parseFloat(value).toFixed(this.stepDepth);
        return this.prefix + val + this.postfix
      }
    },
    getColourFromValue(val) {
      var domain = []
      domain.push(this.min)
      for (var i = 1; i < this.colours.length - 1; i++) {
        domain.push(this.max / this.colours.length * i)
      };
      domain.push(this.max)
      var col = scaleLinear()
        .domain(domain)
        .range(this.colours)(val);
      return col
    },
    updateSliderSize() {
      this.clientX = this.$el.clientWidth
      this.clientY = this.$el.clientHeight
      this.minPx = this.handle['min' + this.dimension]
      this.maxPx = this.handle['max' + this.dimension]
      this.handleRadius = parseInt(this.handle.target.getBoundingClientRect()[this.vertical ? 'height' : 'width'] / 2)
    },
    steps(value) {

      var step = (this.maxPx / (this.max - this.min)) * this.step
      return Math.round(value / step) * step
    },
    setPosition(e) {
      var pos = this.steps(this.handle['end' + this.dimension])
      this.redrawBar(pos, true)
      this.posPx = pos
    },
    getPositionFromValue() {
      if (this.value === null) return false
      var pos = ((this.value - this.min) / (this.max - this.min) * this.maxPx)
      if (this.reverse) return this.maxPx - pos
      return pos
    },
    setPositionFromValue() {
      if (this.value === null) return false
      var pos = this.getPositionFromValue()
      var handleProps = {}
      handleProps[this.dimension.toLowerCase()] = pos
      TweenLite.to(this.$refs.handle, 0.2, handleProps);
      this.redrawBar(pos, true)
      this.posPx = pos
    },
    setPositionDrop(e) {
      var pos = this.steps(this.handle['end' + this.dimension])
      this.posPx = pos
      var handleProps = {}
      handleProps[this.dimension.toLowerCase()] = pos
      TweenLite.to(this.$refs.handle, 0.2, handleProps);
      this.redrawBar(pos, true)
    },
    getWithinRange(number) {
      if (number < this.minPx) return this.minPx
      if (number > this.maxPx) return this.maxPx
      if (this.step) number = this.steps(number)
      return number
    },
    // clickHandle(e){
    //     e.stopPropagation()
    //     e.preventDefault()
    //     console.log('clickHandle');
    // },
    redrawBar(pos, animate) {
      var barProps = {}
      if (this.reverse) {
        var prop1 = this.dimension == 'X' ? 'x' : 'y'
        var prop2 = this.dimension == 'X' ? 'width' : 'height'
        barProps[prop1] = pos + 'px'
        barProps[prop2] = ((this.maxPx + this.handleRadius) - pos) + 'px'

      } else {
        var prop1 = this.dimension == 'X' ? 'width' : 'height'
        barProps[prop1] = pos + 'px'

      }
      TweenLite.to(this.$refs.sliderBar, animate ? 0.2 : 0, barProps);
    },
    clickSliderTrack(e) {
      if (this.disabled || e.target.classList.contains('handle')) return;
      e.preventDefault()
      e.stopPropagation()
      this.handleRadius = parseInt(this.handle.target.getBoundingClientRect()[this.vertical ? 'height' : 'width'] / 2)
      var pos = this.getWithinRange(e['offset' + this.dimension] - this.handleRadius)
      var handleProps = {}
      handleProps[this.dimension.toLowerCase()] = pos
      TweenLite.to(this.$refs.handle, 0.2, handleProps);
      this.redrawBar(pos, true)
      this.posPx = pos
      this.handle.update()
    }
  },
  watch: {
    $mq() {
      this.resize()
    },

    steps() {
      if (this.handle) {
        // this.handle.vars.liveSnap = this.steps
        this.handle.update()
      }
    },
    value(val) {
      if (val !== this.posValue) this.setPositionFromValue()
    },
    posPx(val) {
      this.$emit('input', this.posValue)
    },
    disabled() {}
  }

}

</script>
<style lang="scss">
$sliderLength:400px;
$popoverSize:40px;
$sliderRadius:13.5px;
$gridDepth:10px;

.slider-container {
  position: relative;
  margin-bottom: 1em;
  height: $popoverSize + ($sliderRadius * 2) + ($gridDepth * 3) + 10;

  &.disabled {
    opacity: 0.6;

    .slider-popover,
    .handle,
    .slider-bar {
      display: none;
    }

  }

  &.unanswered {

    .slider-popover,
    .handle,
    .slider-bar {
      display: none;
    }

  }
}

.popover-track {
  position: relative;
  height: $popoverSize;

  .slider-popover {
    position: absolute;
    padding: 4px;
    border-radius: 4px;
    background: #638cae;
    text-shadow: 0px 2px 2px #062741;
    color: white;
    border: thin solid #ccc;

    &:empty {
      display: none;
    }
  }
}

.slider {
  position: relative;
  height: $sliderRadius * 2;

  .slider-track,
  .slider-bar {
    position: absolute;
    width: calc(100% - #{$sliderRadius});
    height: 50%;
    top: 25%;
    z-index: 1;
    left: $sliderRadius / 2;
    cursor:pointer;
  }

  .slider-bar {
    position: absolute;
    width: 0px;
    height: 50%;
    top: 25%;
    z-index: 1;
    left: $sliderRadius / 2;
  }
}

.slider-track {
  border-radius: $sliderRadius;
  border: 1px solid #CCC;
  background: linear-gradient(to bottom, #DDD -50%, #FFF 150%);
  box-shadow: inset 0px 1px 2px 1px rgba(0, 0, 0, 0.2), inset 0px 1px 13px 0px rgba(0, 0, 0, 0.1);

}

.slider-bar {
  box-shadow: inset -1px -1px 2px 3px rgba(0, 0, 0, 0.2), inset 5px -7px 12px 3px rgba(255, 255, 255, 0.3), -1px 2px 4px 0px rgba(111, 111, 111, 0.3);
  pointer-events: none;
  border-radius: $sliderRadius;
  background: linear-gradient(to top, #428bca 0%, #7fc3e8 100%);
}

.slider-container-vertical {
  float: left;
  width: ($popoverSize * 2) + ($sliderRadius * 2) + ($gridDepth * 3) + $sliderRadius;
  height: $sliderLength;
  margin-right: 1em;

  .popover-track {
    float: left;
    left: 0;
    width: $popoverSize * 2;
    height: $sliderLength;

    .slider-popover {
      right: 0;
    }
  }

  .slider {
    float: left;
    width: $sliderRadius * 2;
    height: $sliderLength;

    .slider-track,
    .slider-bar {
      width: 50%;
      height: calc(100% - #{$sliderRadius} * 2);
      left: 25%;
      top: $sliderRadius / 2;
    }

    .slider-track {
      background: linear-gradient(to right, #DDD -50%, #FFF 150%);
      box-shadow: inset 1px 0px 2px 1px rgba(0, 0, 0, 0.2), inset 2px 0px 13px 0px rgba(0, 0, 0, 0.1);
    }

    .slider-bar {
      box-shadow: inset -1px -1px 2px 3px rgba(0, 0, 0, 0.2), inset -7px 5px 12px 3px rgba(255, 255, 255, 0.3), 2px -1px 4px 0px rgba(111, 111, 111, 0.3);
      background: linear-gradient(to left, #428bca 0%, #7fc3e8 100%);

    }
  }
}


.handle {
  position: absolute;
  cursor: pointer;
  width: $sliderRadius * 2;
  height: $sliderRadius * 2;
  border: 1px solid #AAA;
  border-radius: 50%;
  background: linear-gradient(to bottom, white 0%, gainsboro 20%, white 100%);
  box-shadow: 0px 3px 11px 3px rgba(0, 0, 0, 0.1), 0px 3px 2px 0px rgba(0, 0, 0, 0.2), inset 1px -4px 4px 1px rgba(0, 0, 0, 0.2);
  z-index: 10;

  &:hover {
    background: linear-gradient(to bottom, white 0%, gainsboro 50%, white 100%);
  }
}

.grid {
  position: relative;

  width: calc(100% - #{$sliderRadius}*2);
  height: $gridDepth;
  left: $sliderRadius;
  top: - $gridDepth +3px;
  display: flex;
  flex-direction: row;
  margin-bottom: 10px;

  &.grid-reverse {
    flex-direction: row-reverse;

    .grid-unit:last-child {
      flex: auto;
    }

    .grid-unit:first-child {
      flex: none;
    }
  }

  .grid-unit {
    border-left: 1px solid #777;
    flex: auto;
    justify-content: center;
    position: relative;

    &:last-child {
      flex: none;
    }

    background: linear-gradient(to right, transparent, transparent 24%, #d0cece 25%, transparent 26%, transparent 49%, #d0cece 50%, transparent 51%, transparent 74%, #d0cece 75%, transparent 76%);
    background-size: 100% 50%;
    background-repeat-y: no-repeat;
  }

  .grid-num {
    position: absolute;
    font-size: 12px;

  }

  &:not(.grid-vertical) {
    .grid-num {
      width: 100%;
      margin-left: -50%;
      text-align: center;
      top: 0.8em;
      left: 0;
    }

    .grid-unit:last-of-type {
      .grid-num {
        width: 5em;
        left: -2.5em;
      }
    }

    &.grid-reverse .grid-unit:last-of-type {
      .grid-num {
        width: 5em;
        left: 2.5em;
      }
    }
  }

  &.grid-vertical {
    float: left;
    left: - $gridDepth+3px;
    top: $sliderRadius;
    width: $gridDepth;
    height:calc(100% - #{$sliderRadius}*2);
    flex-direction: column;

    .grid-unit {
      border-left: none;
      border-top: 1px solid #ccc;
      background: linear-gradient(to bottom, transparent, transparent 23%, #d0cece 25%, transparent 27%, transparent 48%, #d0cece 50%, transparent 52%, transparent 73%, #d0cece 75%, transparent 77%);
      background-size: 50% 100%;
      background-repeat-x: no-repeat;
    }

    .grid-num {
      left: 1.4em;
      top: -0.8em;
    }

    &.grid-reverse {
      flex-direction: column-reverse;
    }

  }

}

</style>
